import { useState, type FormEvent, type ReactNode } from 'react';
import type { CallerCheck, CallerCheckUpsert, Destination, Lookup, LookupUpsert } from '@arcnet/contracts';
import { useSaveCallerCheck, useSaveLookup, useTestLookup } from '../../lib/api/hooks-tms.js';
import { EditorHead, ErrorBanner, Segmented, SubHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { DestinationPicker } from '../../components/telephony/DestinationPicker.js';
import type { PanelMode } from './flows/panel.js';

/**
 * The editors for a caller check and a look up (docs/41 §16): on their own page under Call steps,
 * and in a call flow's side panel (`panel`), where the result goes into the draft instead of being
 * saved and may be unfinished -- publishing is what refuses an unfinished step.
 */

const NUMBER = /^\+?[0-9]{2,15}\*?$/;

/** One number per line (or comma): what the save takes, and the lines that are not numbers. */
function numbersFrom(text: string): { numbers: string[]; bad: string[] } {
  const all = text.split(/[\n,;]+/).map((x) => x.replace(/[\s()-]/g, '')).filter(Boolean);
  return { numbers: [...new Set(all.filter((x) => NUMBER.test(x)))], bad: all.filter((x) => !NUMBER.test(x)) };
}

export function CallerCheckEditor({ tenantId, existing, onDone, panel, headAction }: {
  tenantId: string;
  existing: (Partial<CallerCheck> & { name: string }) | null;
  onDone: () => void;
  panel?: PanelMode;
  headAction?: ReactNode;
}) {
  const save = useSaveCallerCheck(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [text, setText] = useState((existing?.numbers ?? []).join('\n'));
  const [withheld, setWithheld] = useState(existing?.match_withheld ?? false);
  const [match, setMatch] = useState<Destination | null>(existing?.match_destination ?? null);
  const [other, setOther] = useState<Destination | null>(existing?.no_match_destination ?? null);
  const { numbers, bad } = numbersFrom(text);

  const missing: string[] = [];
  if (!name.trim()) missing.push('a name');
  if (numbers.length === 0 && !withheld) missing.push('a number to match');
  if (bad.length > 0) missing.push(`numbers it can read (${bad.slice(0, 3).join(', ')}${bad.length > 3 ? '…' : ''})`);
  if (numbers.length > 200) missing.push('200 numbers at most');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body: CallerCheckUpsert = {
      name: name.trim(), numbers, match_withheld: withheld, match_destination: match, no_match_destination: other,
    };
    if (panel) { panel.onApply(body); return; }
    save.mutate({ ...(existing?.id ? { id: existing.id } : {}), ...body }, { onSuccess: onDone });
  };

  return (
    <form id={panel?.formId} onSubmit={submit}>
      {!panel && <EditorHead back="Call steps" onBack={onDone} title={existing?.id ? existing.name : 'New caller check'} action={headAction} />}
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-field" style={{ maxWidth: 380 }}>
          <label className="arc-label" htmlFor="cc-name">Name</label>
          <input id="cc-name" className="arc-input" required maxLength={120} value={name}
            onChange={(e) => setName(e.target.value)} placeholder="VIP customers" />
        </div>
        <div className="arc-field" style={{ maxWidth: 380 }}>
          <label className="arc-label" htmlFor="cc-numbers">Callers on the list</label>
          <textarea id="cc-numbers" className="arc-input arc-mono" rows={6} value={text}
            onChange={(e) => setText(e.target.value)} placeholder={'0821234567\n0861*\n+44*'} aria-invalid={bad.length > 0 || undefined} />
          <p className="arc-hint">
            One number per line. End one with * to match every number that starts with it. 082…, 2782… and +2782… are the same number.
          </p>
        </div>
        <label className="arc-row" style={{ gap: 8 }}>
          <input type="checkbox" checked={withheld} onChange={(e) => setWithheld(e.target.checked)} />
          Callers who hide their number count as on the list
        </label>
      </div>
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="Either may be left empty: the call ends on that branch.">Where calls go</SubHead>
        <div className="arc-route-dests">
          <span className="arc-route-dest-when">On the list</span>
          <DestinationPicker tenantId={tenantId} value={match} onChange={setMatch} allowNone />
          <span className="arc-route-dest-when arc-muted">Anyone else</span>
          <DestinationPicker tenantId={tenantId} value={other} onChange={setOther} allowNone />
        </div>
      </div>
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {!panel && (
          <>
            <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || missing.length > 0}>
              {save.isPending ? 'Saving…' : existing?.id ? 'Save changes' : 'Create caller check'}
            </button>
            <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
          </>
        )}
        {missing.length > 0 && <span className="arc-subtle" style={{ fontSize: 12 }}>Needs {missing.join(', ')}.</span>}
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}

type Case = { value: string; destination: Destination | null };

export function LookupEditor({ tenantId, existing, onDone, panel, headAction }: {
  tenantId: string;
  existing: (Partial<Lookup> & { name: string }) | null;
  onDone: () => void;
  panel?: PanelMode;
  headAction?: ReactNode;
}) {
  const save = useSaveLookup(tenantId);
  const test = useTestLookup(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [method, setMethod] = useState<'GET' | 'POST'>(existing?.method ?? 'GET');
  const [headerName, setHeaderName] = useState(existing?.header_name ?? '');
  // Undefined: keep what is stored. A string: replace it. Null: remove it.
  const [headerValue, setHeaderValue] = useState<string | null | undefined>(undefined);
  const stored = (existing?.has_header_value ?? false) && headerValue === undefined;
  const [field, setField] = useState(existing?.field ?? '');
  const [cases, setCases] = useState<Case[]>(existing?.cases?.length ? existing.cases : [{ value: '', destination: null }]);
  const [other, setOther] = useState<Destination | null>(existing?.no_match_destination ?? null);
  const [error, setError] = useState<Destination | null>(existing?.error_destination ?? null);
  const [caller, setCaller] = useState('0821234567');

  const filled = cases.filter((c) => c.value.trim());
  const missing: string[] = [];
  if (!name.trim()) missing.push('a name');
  if (!/^https:\/\/[^\s/]+/.test(url.trim())) missing.push('an https:// address');
  if (!/^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+){0,4}$/.test(field.trim())) missing.push('the field to read');
  if (filled.length === 0) missing.push('an answer to route on');
  if (headerName.trim() && !/^[A-Za-z0-9-]{1,64}$/.test(headerName.trim())) missing.push('a header name of letters, digits and dashes');

  const setCase = (i: number, c: Partial<Case>) => setCases(cases.map((x, j) => (j === i ? { ...x, ...c } : x)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body: LookupUpsert = {
      name: name.trim(), url: url.trim(), method, header_name: headerName.trim() || null,
      ...(headerValue !== undefined ? { header_value: headerValue || null } : {}),
      field: field.trim(), cases: filled.map((c) => ({ value: c.value.trim(), destination: c.destination })),
      no_match_destination: other, error_destination: error,
    };
    if (panel) { panel.onApply(body); return; }
    save.mutate({ ...(existing?.id ? { id: existing.id } : {}), ...body }, { onSuccess: onDone });
  };

  const sample = method === 'GET'
    ? `GET ${url.trim() || 'https://…'}?caller=0821234567&dialled=0110001234`
    : `POST ${url.trim() || 'https://…'}\n{"caller":"0821234567","dialled":"0110001234"}`;
  const t = test.data;

  return (
    <form id={panel?.formId} onSubmit={submit}>
      {!panel && <EditorHead back="Call steps" onBack={onDone} title={existing?.id ? existing.name : 'New look up'} action={headAction} />}
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-field" style={{ maxWidth: 380 }}>
          <label className="arc-label" htmlFor="lk-name">Name</label>
          <input id="lk-name" className="arc-input" required maxLength={120} value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Customer tier" />
        </div>
        <SubHead sub="Asked when each call arrives. No answer within 3 seconds takes the No answer branch, so a slow system never holds a caller longer.">Your system</SubHead>
        <div className="arc-field">
          <label className="arc-label" htmlFor="lk-url">Address</label>
          <input id="lk-url" className="arc-input arc-mono" style={{ maxWidth: 560 }} type="url" maxLength={500} value={url}
            onChange={(e) => setUrl(e.target.value)} placeholder="https://crm.example.co.za/api/caller" />
          <p className="arc-hint">HTTPS on the standard port only. Private and internal addresses are refused.</p>
        </div>
        <Segmented label="Method" value={method} options={[['GET', 'GET'], ['POST', 'POST']]} onChange={setMethod} />
        <p className="arc-hint" style={{ marginTop: 8 }}>What is sent:</p>
        <pre className="arc-mono" style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: '0 0 12px' }}>{sample}</pre>
        <div className="arc-grid arc-grid-2">
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="lk-hname">Header (optional)</label>
            <input id="lk-hname" className="arc-input arc-mono" maxLength={64} value={headerName}
              onChange={(e) => setHeaderName(e.target.value)} placeholder="X-Api-Key" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="lk-hvalue">Header value</label>
            <input id="lk-hvalue" className="arc-input" type="password" autoComplete="off" maxLength={500}
              disabled={!headerName.trim()} value={headerValue ?? ''}
              onChange={(e) => setHeaderValue(e.target.value)}
              placeholder={stored ? 'Stored. Type to replace it.' : ''} />
            <p className="arc-hint">
              Kept encrypted and never shown again.
              {stored && (
                <> <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" style={{ padding: 0 }} onClick={() => setHeaderValue(null)}>Remove it</button></>
              )}
            </p>
          </div>
        </div>
        <div className="arc-field" style={{ maxWidth: 380, marginTop: 12 }}>
          <label className="arc-label" htmlFor="lk-field">Field to read</label>
          <input id="lk-field" className="arc-input arc-mono" maxLength={120} value={field}
            onChange={(e) => setField(e.target.value)} placeholder="tier" />
          <p className="arc-hint">The answer must be JSON. Use a dot for a field inside another: customer.tier.</p>
        </div>
      </div>

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="Matched ignoring capitals. Up to ten. A branch left empty ends the call.">Where calls go</SubHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 180px) minmax(0, 420px) 32px', gap: 8, alignItems: 'center' }}>
          {cases.map((c, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <input className="arc-input" aria-label={`Answer ${i + 1}`} maxLength={100}
                value={c.value} onChange={(e) => setCase(i, { value: e.target.value })} placeholder="gold" />
              <DestinationPicker tenantId={tenantId} value={c.destination} onChange={(d) => setCase(i, { destination: d })} allowNone />
              {cases.length > 1
                ? <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label={`Remove answer ${i + 1}`}
                    onClick={() => setCases(cases.filter((_, j) => j !== i))}><Icon name="close" size={12} /></button>
                : <span />}
            </div>
          ))}
          <span className="arc-muted">Any other answer</span>
          <DestinationPicker tenantId={tenantId} value={other} onChange={setOther} allowNone />
          <span />
          <span className="arc-muted">No answer</span>
          <DestinationPicker tenantId={tenantId} value={error} onChange={setError} allowNone />
          <span />
        </div>
        {cases.length < 10 && (
          <button type="button" className="arc-btn arc-btn-sm" style={{ marginTop: 10 }}
            onClick={() => setCases([...cases, { value: '', destination: null }])}><Icon name="plus" size={12} /> Add an answer</button>
        )}
        <p className="arc-hint">No answer covers an error, a timeout, an answer that is not JSON, and a missing field.</p>
      </div>

      {existing?.id && (
        <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
          <SubHead sub={panel ? 'Uses the published settings, not the draft.' : 'Uses the saved settings.'}>Try it</SubHead>
          <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input className="arc-input arc-mono" style={{ maxWidth: 170 }} aria-label="Caller's number" value={caller}
              onChange={(e) => setCaller(e.target.value)} />
            <button type="button" className="arc-btn arc-btn-sm" disabled={test.isPending}
              onClick={() => test.mutate({ id: existing.id!, caller: caller.trim() })}>
              {test.isPending ? 'Asking…' : 'Ask now'}
            </button>
          </div>
          {t && (
            <p className="arc-hint" style={{ marginTop: 8 }}>
              {t.outcome === 'case' ? `Answered “${t.value ?? ''}” in ${t.ms} ms: goes the “${existing.cases?.[t.case_index ?? 0]?.value ?? ''}” way.`
                : t.outcome === 'no_match' ? `Answered “${t.value ?? ''}” in ${t.ms} ms: no answer matches, so it goes the Any other answer way.`
                  : `${t.error ?? 'It failed'} The call would go the No answer way.`}
            </p>
          )}
          {test.error && <ErrorBanner error={test.error} />}
        </div>
      )}

      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {!panel && (
          <>
            <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || missing.length > 0}>
              {save.isPending ? 'Saving…' : existing?.id ? 'Save changes' : 'Create look up'}
            </button>
            <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
          </>
        )}
        {missing.length > 0 && <span className="arc-subtle" style={{ fontSize: 12 }}>Needs {missing.join(', ')}.</span>}
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}
